"use client";

import { motion } from "motion/react";
import { BookOpen, Calendar, ChevronRight, Pencil, SchoolIcon } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function EducationDisplay() {
  const { profileData } = useProfileDataStore();
  const data = profileData?.education_section;
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const { open } = useProfileEditDialogStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<any>(null);

  const list = data ?? [];
  const displayedEducation = isExpanded ? list : list.slice(0, 4);
  const hasMore = list.length > 4;

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [isExpanded]);

  if (!data) return null;

  return (
    <Card
      ref={containerRef}
      className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-emerald-400/50 shadow-2xl backdrop-blur-xl"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-15 -right-20 h-36 w-36 rounded-full bg-gradient-to-br from-emerald-400 to-green-600" />
        <div className="absolute -bottom-20 -left-20 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
      </div>

      <CardHeader className="relative flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl p-2">
            <Icon
              className={"text-emerald-400"}
              height={"1.25rem"}
              icon={"mdi:book-education-outline"}
              width={"1.25rem"}
            />
          </div>
          {t("profile:educationSection.title")}
        </motion.h2>
        {data.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={
                    "bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 rounded-xl backdrop-blur-sm"
                  }
                  size="sm"
                  variant="ghostSecondary"
                  onClick={() => open("education")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{t("common:edit")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardHeader>
      <CardContent className="relative space-y-6">
        {data.length === 0 && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="relative h-[120px] w-full space-y-2 rounded-2xl border border-dashed border-emerald-300/50 bg-emerald-500/5 backdrop-blur-sm"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Button
              className="absolute top-0 left-0 flex h-full w-full items-center rounded-2xl bg-transparent hover:bg-emerald-500/10"
              onClick={() => open("education")}
            >
              <Pencil className="mr-3 h-6 w-6 text-emerald-400" />
              <h3 className="text-accent-foreground text-lg font-medium">
                {t("profile:educationSection.add")}
              </h3>
            </Button>
          </motion.div>
        )}
        {displayedEducation.map((education, index) => (
          <motion.div
            key={education.id}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-2"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            {index > 0 && <Separator className="my-4 bg-emerald-200/20" />}
            <div className="rounded-2xl bg-transparent p-4 backdrop-blur-sm transition-colors hover:bg-emerald-500/2">
              <div className="flex items-start space-x-2">
                <div className="mt-1 rounded-xl p-2">
                  <SchoolIcon className="h-5 w-5 text-emerald-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-accent-foreground text-lg font-semibold">
                    {education.institution_name}
                  </h3>
                  <div className={"flex flex-wrap gap-3"}>
                    {education.degree && (
                      <div className="rounded-xl bg-emerald-500/5 px-3 py-1">
                        <p className="text-sm font-medium text-emerald-300">{education.degree}</p>
                      </div>
                    )}
                    {education.field_of_study && (
                      <p className="text-muted-foreground flex items-center text-sm">
                        <BookOpen className="mr-2 h-4 w-4" />
                        {education.field_of_study}
                      </p>
                    )}
                  </div>
                  {(education.start_date || education.end_date) && (
                    <p className="text-muted-foreground flex items-center text-sm">
                      <Calendar className="mr-2 h-4 w-4" />
                      {education.start_date && education.start_date} {education.end_date && " - "}
                      {education.end_date && education.end_date}
                    </p>
                  )}
                  {education.description && (
                    <div className="mt-2 rounded-xl bg-emerald-500/5 p-3">
                      <p className={"text-muted-foreground mb-1 text-xs font-medium"}>
                        {t("profile:educationSection.display.description")}
                      </p>
                      <p className="text-secondary-foreground text-sm">{education.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
        {hasMore && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ delay: 0.2 }}>
            <Button
              className="mt-2 w-full justify-center gap-2 rounded-xl border-none bg-transparent text-emerald-300 hover:bg-emerald-500/15"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>
                {isExpanded ? t("common:showLess") : t("common:showAll", { count: list.length })}
              </span>
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            </Button>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
